/**
 * useAutoLogout — enforce the user's `systemSettings.autoLogout` preference.
 *
 * Background (BUG-S1043-003 / FINDING-S980-A family — saved-but-not-consumed):
 *   /settings/general saves `autoLogout` (minutes, as string) to system_settings.general_system,
 *   but no code consumed it. The user could set "Auto Logout: 30 min" and it had no effect —
 *   a security/compliance gap (audit assumed the limit was enforced).
 *
 * Behavior:
 *   - Read autoLogout setting on mount (minutes). Treat 0/empty/invalid as DISABLED.
 *   - Subscribe to activity events (mousemove, keydown, click, scroll, touchstart) → reset timer.
 *   - On idle exceeding the configured minutes, invoke onLogout() once.
 *   - Cleans up listeners + timer on unmount.
 *
 * Usage (inside AuthProvider, only when authenticated):
 *   useAutoLogout(isAuthenticated, logout);
 */
import { useEffect, useRef } from 'react';
import settingsService from '../services/settingsService';

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'] as const;

// Min/max bounds to defend against bad config (e.g. 0.001 min or 99999 min).
export const MIN_MINUTES = 1;
export const MAX_MINUTES = 24 * 60; // 1 day

export function useAutoLogout(
  enabled: boolean,
  onLogout: () => void
): void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onLogoutRef = useRef(onLogout);
  onLogoutRef.current = onLogout;

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let idleMs = 0;

    const fireLogout = () => {
      // Guard against duplicate fires if events keep arriving after timeout.
      if (cancelled) return;
      cancelled = true;
      onLogoutRef.current();
    };

    const resetTimer = () => {
      if (cancelled || idleMs <= 0) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(fireLogout, idleMs);
    };

    const load = async () => {
      try {
        const s = await settingsService.getGeneralSettings();
        const raw = s?.systemSettings?.autoLogout;
        const minutes = parseInt(String(raw ?? ''), 10);
        if (!minutes || minutes < MIN_MINUTES || minutes > MAX_MINUTES) {
          idleMs = 0; // disabled or invalid
          return;
        }
        idleMs = minutes * 60 * 1000;
        // Initial timer
        resetTimer();
        // Subscribe to activity events to reset
        ACTIVITY_EVENTS.forEach((evt) =>
          window.addEventListener(evt, resetTimer, { passive: true })
        );
      } catch (err) {
        // Non-blocking: failure to read setting = feature off. Surface as debug only.
        // eslint-disable-next-line no-console
        console.debug('useAutoLogout: could not load setting:', err);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, resetTimer)
      );
    };
  }, [enabled]);
}

export default useAutoLogout;
